import { API, ApiArgs } from '@/api/API'
import type { EventDetails } from '@/api/Helpers'
import { firstEntity, LinkedTG, TicketGroupsAndTypesResponse, toLinkedGroups } from '@/api/Helpers'
import type { Session } from '@/api/Sessions'
import deepmerge from 'deepmerge'
import moment from 'moment-timezone'
import { isTrueish } from './StringHelpers'

// TODO Return a new sorted list instead of modifying in place.
export function sortEventSessionsByDate(eventSessions) {
  eventSessions.sort((a, b) => {
    return a.start_datetime > b.start_datetime ? 1 : -1
  })
}

type LinkedSessionTG = LinkedTG & SessionTG

export function getSessionTicketGroupsAndTypes(id: string, tgIDs: string[]): Promise<LinkedSessionTG[]> {
  const path = `event_session/${id}`

  const args: ApiArgs = {
    embed: 'ticket_group,ticket_type,ticket_group.meta',
    query: {
      'ticket_group.id._in': tgIDs.join(','),
      'ticket_group.hidden_type._in': 'public_browsable,public_member_only,public_link_only',
    },
  }

  // It is easier to filter out entities before linking them together to avoid leaks
  return API.getCached<'event_session' | 'ticket_group' | 'ticket_type' | 'meta'>(path, args)
    .then(filterUnavailable)
    .then(toLinkedGroups) as Promise<LinkedSessionTG[]>
}

export function getSession(id: string): Promise<{ session: EventSession; venue: Venue }> {
  const path = `event_session/${id}`
  return API.getCached<'event_session' | 'venue'>(path, { embed: 'venue' }).then((response) => {
    const [session, venue] = firstEntity(response, 'event_session', 'venue')
    return {
      session,
      venue,
    }
  })
}

function filterUnavailable(original: TicketGroupsAndTypesResponse): TicketGroupsAndTypesResponse {
  const overwrites: DeepPartial<TicketGroupsAndTypesResponse> = {
    ticket_type: {
      _data: original.ticket_type._data.filter((type) => type.available),
    },
    ticket_group: {
      _data: (original.ticket_group._data as SessionTG[])
        .filter((group) => group.available)
        .filter((group) => !group.sales_end || moment().isBefore(group.sales_end))
        .filter((group) => !group.sales_start || moment().isAfter(group.sales_start)),
    },
  }

  // Configures deepmerge to merge objects and overwrite arrays, instead of merging both.
  // @see https://www.npmjs.com/package/deepmerge#arraymerge-example-overwrite-target-array
  const arrayMerge = (destination, source) => source
  return deepmerge(original, overwrites, { arrayMerge }) as TicketGroupsAndTypesResponse
}

export function formatSessionList(sessions: Session[]): string {
  return sessions.map((session) => session.startMoment.format('h:mm A')).join(', ')
}

/**
 * TODO Replace Moment
 *
 * Probably wait for Temporal support to land and IE11 support to be dropped.
 *
 * @see https://trello.com/c/1HO9Mda7
 * @see https://tixtrackteam.slack.com/archives/CP5526CDN/p1627011010086200
 * @see https://moment.github.io/luxon/docs/manual/why.html
 *
 * The builtin `Intl.DateTimeFormat` JavaScript API doesn't support timezones
 * as part of the date data (`Date` object). Luxon's `DateTime` objects do support this.
 *
 * Consider also rolling our own E.g.;
 *
 * ```
 * export default class DateWithTimezone() {
 *   date: Date
 *
 *   constructor(datetime, public timeZone: string) {
 *     this.date = new Date(datetime)
 *   }
 *
 *   format(options): string {
 *     return Intl.DateTimeFormat({timeZone: this.timeZone, ...options).format(this.date)
 *   }
 * }
 * ```
 */
export function normalizeSession(session: EventSession, venue: Venue): Session {
  const { capacity, used_capacity, oversell_capacity, end_datetime, start_datetime } = session
  const tz = venue.timezone

  return {
    ...session,

    startMoment: moment(start_datetime).tz(tz),
    endMoment: moment(end_datetime).tz(tz),

    // Oversell capacity allows any remaining ticket(s) to be sold together with up to N "over-sold" tickets.
    // This helps prevent sessions from being undersold, when single tickets are difficult to sell.
    availableCapacity: handleUnlimitedCapacity(capacity) + oversell_capacity - used_capacity,
  }
}

export function handleUnlimitedCapacity(capacity: number): number {
  // Unlimited capacity is represented by a capacity of -1.
  return capacity === -1 ? Infinity : capacity
}

export function normalizeSessions(response: ApiResponse<'event_session'>, venue) {
  return response.event_session._data.map((session) => normalizeSession(session, venue))
}

export function getHideSoldOutSessionsConfig(event: EventDetails, env: TixEnvironment) {
  return event.meta.hide_sold_out_sessions !== undefined
    ? isTrueish(event.meta.hide_sold_out_sessions)
    : env.web.hide_sold_out_sessions
}

export function sessionsWithPriceIncrease(
  sessions: Session[],
  priceSchedules: PriceSchedule[],
  currentScheduledPrice: number,
): Session[] {
  let i = 0
  return sessions.map((session) => {
    // Price schedules are guaranteed to contain all sessions and not overlap each other.
    while (!session.startMoment.isBetween(priceSchedules[i].from, priceSchedules[i].to, undefined, '[)')) {
      i++
    }

    return {
      ...session,
      priceIncrease: Number(priceSchedules[i]?.currency_amount) - currentScheduledPrice,
    }
  })
}
